import React from 'react';
import { motion } from 'framer-motion';
import { FaAws, FaMicrosoft, FaGoogle, FaGithub, FaGitlab, FaBitbucket, FaJenkins } from 'react-icons/fa';
import { SiArgo, SiTerraform, SiPrometheus, SiDatadog } from 'react-icons/si';
import ScrollHeader from '../components/ScrollHeader';
import ImageModal from '../components/ImageModal';

const Integrations: React.FC = () => {
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  const [expandedSection, setExpandedSection] = React.useState<string | null>('aws');

  const cloudProviders = [
    {
      id: 'aws',
      name: 'Amazon Web Services (AWS)',
      icon: <FaAws size={32} />,
      color: '#FF9900',
      description: 'Deploy to AWS using IAM credentials with appropriate permissions'
    },
    {
      id: 'azure',
      name: 'Microsoft Azure',
      icon: <FaMicrosoft size={32} />,
      color: '#0078D4',
      description: 'Connect using Azure Service Principals with contributor access'
    },
    {
      id: 'gcp',
      name: 'Google Cloud Platform',
      icon: <FaGoogle size={32} />,
      color: '#4285F4',
      description: 'Integrate using GCP Service Accounts with necessary IAM roles'
    }
  ];

  const otherIntegrations = [
    {
      name: 'GitHub',
      icon: <FaGithub size={28} />,
      color: '#24292e',
      description: 'Version control and CI/CD integration',
      features: ['Repository management', 'GitHub Actions', 'Pull requests', 'Issue tracking']
    },
    {
      name: 'GitLab',
      icon: <FaGitlab size={28} />,
      color: '#FC6D26',
      description: 'Complete DevOps platform integration',
      features: ['CI/CD pipelines', 'Container registry', 'Security scanning', 'Issue boards']
    },
    {
      name: 'Bitbucket',
      icon: <FaBitbucket size={28} />,
      color: '#0052CC',
      description: 'Git repository and pipeline integration',
      features: ['Repository hosting', 'Bitbucket Pipelines', 'Code review', 'Branch permissions']
    },
    {
      name: 'Jenkins',
      icon: <FaJenkins size={28} />,
      color: '#D33B33',
      description: 'Automation server for CI/CD',
      features: ['Build automation', 'Pipeline as code', 'Plugin ecosystem', 'Distributed builds']
    },
    {
      name: 'ArgoCD',
      icon: <SiArgo size={28} />,
      color: '#EF7B4D',
      description: 'GitOps continuous delivery for Kubernetes',
      features: ['Declarative GitOps', 'Automated sync', 'Multi-cluster', 'Rollback support']
    },
    {
      name: 'Terraform Cloud',
      icon: <SiTerraform size={28} />,
      color: '#7B42BC',
      description: 'Infrastructure as Code management',
      features: ['Remote state', 'Workspace management', 'Policy as code', 'Cost estimation']
    },
    {
      name: 'Datadog',
      icon: <SiDatadog size={28} />,
      color: '#632CA6',
      description: 'Monitoring and analytics platform',
      features: ['Infrastructure monitoring', 'APM', 'Log management', 'Alerting']
    },
    {
      name: 'Prometheus',
      icon: <SiPrometheus size={28} />,
      color: '#E6522C',
      description: 'Monitoring and alerting toolkit',
      features: ['Time-series database', 'PromQL queries', 'Alertmanager', 'Service discovery']
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <ScrollHeader title="Integrations" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Connect Ana to your cloud providers and DevOps tools to enable infrastructure deployment and management.
          </p>

          <div className="bg-primary-500/10 dark:bg-primary-500/20 border-l-4 border-primary-500 p-6 rounded-r-lg mb-8">
            <h3 className="text-primary-600 dark:text-primary-400 mt-0 mb-2">💡 Key Concept</h3>
            <p className="mb-0">
              Integrations allow Ana to deploy and manage infrastructure on your behalf. You must set up 
              at least one cloud provider integration before you can deploy infrastructure from the 
              Visualisation tool.
            </p>
          </div>

          <h2>Overview</h2>
          <p>
            The Integrations page is where you configure connections to cloud providers and DevOps tools. 
            These integrations provide Ana with the necessary credentials to deploy and manage your infrastructure.
          </p>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/integrations/integrations.png')}
          >
            <img 
              src="/screenshots/integrations/integrations.png" 
              alt="Integrations Overview" 
              className="w-full"
            />
          </div>

          <h2>Cloud Provider Integrations</h2>
          <p>
            Ana supports integration with major cloud providers. Click on each provider below for detailed setup instructions:
          </p>

          <div className="space-y-4 my-8">
            {cloudProviders.map((provider) => (
              <div key={provider.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedSection(expandedSection === provider.id ? null : provider.id)}
                  className="w-full p-6 bg-light-surface dark:bg-dark-surface hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-16 h-16 rounded-lg flex items-center justify-center"
                      style={{ 
                        backgroundColor: `${provider.color}15`,
                        color: provider.color
                      }}
                    >
                      {provider.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-0 mb-1">
                        {provider.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                        {provider.description}
                      </p>
                    </div>
                  </div>
                  <span className="text-2xl text-gray-400">
                    {expandedSection === provider.id ? '−' : '+'}
                  </span>
                </button>

                {expandedSection === provider.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
                  >
                    {provider.id === 'aws' && (
                      <div>
                        <h4 className="text-lg font-semibold mb-4">AWS Integration Setup</h4>
                        
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded-r-lg mb-6">
                          <h5 className="text-yellow-800 dark:text-yellow-400 mt-0 mb-2">📋 Prerequisites</h5>
                          <ul className="text-yellow-700 dark:text-yellow-300 text-sm mb-0 space-y-1">
                            <li>Active AWS account</li>
                            <li>IAM permissions to create users/roles</li>
                            <li>Access to AWS Console</li>
                          </ul>
                        </div>

                        <h5 className="font-semibold mb-3">Step 1: Create IAM User</h5>
                        <ol className="space-y-2 mb-6">
                          <li>Log in to AWS Console</li>
                          <li>Navigate to IAM → Users</li>
                          <li>Click "Add users"</li>
                          <li>Enter username (e.g., "ana-devops-user")</li>
                          <li>Select "Programmatic access"</li>
                        </ol>

                        <h5 className="font-semibold mb-3">Step 2: Attach Permissions</h5>
                        <p className="text-sm mb-3">Choose one of these permission strategies:</p>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg mb-4">
                          <p className="font-semibold text-sm mb-2">Option A: PowerUserAccess (Recommended for Development)</p>
                          <ul className="text-sm space-y-1 mb-0">
                            <li>• Attach "PowerUserAccess" managed policy</li>
                            <li>• Provides broad permissions except IAM management</li>
                          </ul>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg mb-4">
                          <p className="font-semibold text-sm mb-2">Option B: Custom Policy (Recommended for Production)</p>
                          <ul className="text-sm space-y-1 mb-0">
                            <li>• Create custom policy with specific permissions</li>
                            <li>• Include: EC2, S3, RDS, VPC, IAM (limited), CloudFormation</li>
                            <li>• Follow principle of least privilege</li>
                          </ul>
                        </div>

                        <h5 className="font-semibold mb-3">Step 3: Generate Access Keys</h5>
                        <ol className="space-y-2 mb-6">
                          <li>Complete user creation</li>
                          <li>Download or copy the Access Key ID and Secret Access Key</li>
                          <li>⚠️ Store these securely - you won't see the secret key again</li>
                        </ol>

                        <h5 className="font-semibold mb-3">Step 4: Configure in Ana</h5>
                        <ol className="space-y-2 mb-6">
                          <li>Navigate to Integrations page in Ana</li>
                          <li>Click "Add Integration" → Select "AWS"</li>
                          <li>Enter your Access Key ID</li>
                          <li>Enter your Secret Access Key</li>
                          <li>Specify default region (e.g., us-east-1)</li>
                          <li>Click "Test Connection"</li>
                          <li>Save the integration</li>
                        </ol>

                        <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded-r-lg">
                          <h5 className="text-green-800 dark:text-green-400 mt-0 mb-2">✅ Best Practices</h5>
                          <ul className="text-green-700 dark:text-green-300 text-sm mb-0 space-y-1">
                            <li>• Use IAM roles with temporary credentials when possible</li>
                            <li>• Rotate access keys regularly (every 90 days)</li>
                            <li>• Enable MFA for the IAM user</li>
                            <li>• Use separate credentials for dev/staging/production</li>
                            <li>• Never commit credentials to version control</li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {provider.id === 'azure' && (
                      <div>
                        <h4 className="text-lg font-semibold mb-4">Azure Integration Setup</h4>
                        
                        <h5 className="font-semibold mb-3">Step 1: Create Service Principal</h5>
                        <p className="text-sm mb-3">Using Azure CLI:</p>
                        <div className="bg-gray-800 text-gray-100 p-4 rounded-lg mb-4 overflow-x-auto">
                          <code className="text-sm">
                            az ad sp create-for-rbac --name "Ana-DevOps-SP" --role Contributor
                          </code>
                        </div>
                        <p className="text-sm mb-6">This command will output the credentials you need.</p>

                        <h5 className="font-semibold mb-3">Step 2: Assign Permissions</h5>
                        <ol className="space-y-2 mb-6">
                          <li>Navigate to Azure Portal → Subscriptions</li>
                          <li>Select your subscription</li>
                          <li>Go to "Access control (IAM)"</li>
                          <li>Assign "Contributor" role to the service principal</li>
                          <li>Or create custom role with specific permissions</li>
                        </ol>

                        <h5 className="font-semibold mb-3">Step 3: Configure in Ana</h5>
                        <ol className="space-y-2 mb-6">
                          <li>Navigate to Integrations page</li>
                          <li>Click "Add Integration" → Select "Azure"</li>
                          <li>Enter Tenant ID (from CLI output)</li>
                          <li>Enter Client ID / Application ID</li>
                          <li>Enter Client Secret</li>
                          <li>Enter Subscription ID</li>
                          <li>Test and save</li>
                        </ol>

                        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
                          <h5 className="text-blue-800 dark:text-blue-400 mt-0 mb-2">💡 Tip</h5>
                          <p className="text-blue-700 dark:text-blue-300 text-sm mb-0">
                            You can also create the service principal through the Azure Portal under 
                            "Azure Active Directory" → "App registrations"
                          </p>
                        </div>
                      </div>
                    )}

                    {provider.id === 'gcp' && (
                      <div>
                        <h4 className="text-lg font-semibold mb-4">GCP Integration Setup</h4>
                        
                        <h5 className="font-semibold mb-3">Step 1: Create Service Account</h5>
                        <ol className="space-y-2 mb-6">
                          <li>Go to GCP Console → IAM & Admin → Service Accounts</li>
                          <li>Click "Create Service Account"</li>
                          <li>Enter name (e.g., "ana-devops-sa")</li>
                          <li>Add description</li>
                          <li>Click "Create and Continue"</li>
                        </ol>

                        <h5 className="font-semibold mb-3">Step 2: Assign Roles</h5>
                        <p className="text-sm mb-3">Assign appropriate roles:</p>
                        <ul className="space-y-2 mb-6">
                          <li>• <strong>Editor</strong> - For development (broad permissions)</li>
                          <li>• <strong>Compute Admin</strong> - For VM management</li>
                          <li>• <strong>Storage Admin</strong> - For storage operations</li>
                          <li>• <strong>Kubernetes Engine Admin</strong> - For GKE</li>
                          <li>• Or create custom role with specific permissions</li>
                        </ul>

                        <h5 className="font-semibold mb-3">Step 3: Generate Key</h5>
                        <ol className="space-y-2 mb-6">
                          <li>Click on the created service account</li>
                          <li>Go to "Keys" tab</li>
                          <li>Click "Add Key" → "Create new key"</li>
                          <li>Select "JSON" format</li>
                          <li>Download the key file</li>
                          <li>⚠️ Store securely - this file contains sensitive credentials</li>
                        </ol>

                        <h5 className="font-semibold mb-3">Step 4: Configure in Ana</h5>
                        <ol className="space-y-2 mb-6">
                          <li>Navigate to Integrations page</li>
                          <li>Click "Add Integration" → Select "GCP"</li>
                          <li>Upload the JSON key file or paste its contents</li>
                          <li>Specify default project ID</li>
                          <li>Test and save</li>
                        </ol>

                        <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded-r-lg">
                          <h5 className="text-green-800 dark:text-green-400 mt-0 mb-2">🔒 Security</h5>
                          <p className="text-green-700 dark:text-green-300 text-sm mb-0">
                            Treat the JSON key file like a password. Never commit it to version control. 
                            Consider using Workload Identity for GKE workloads instead of service account keys.
                          </p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            ))}
          </div>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/integrations/create_integration.png')}
          >
            <img 
              src="/screenshots/integrations/create_integration.png" 
              alt="Create Integration" 
              className="w-full"
            />
          </div>

          <h2>DevOps Tool Integrations</h2>
          <p>
            Beyond cloud providers, Ana integrates with popular DevOps tools to streamline your workflow:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
            {otherIntegrations.map((integration, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="bg-light-surface dark:bg-dark-surface p-5 rounded-lg border border-light-border dark:border-dark-border hover:border-primary-500 dark:hover:border-primary-400 transition-all"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ 
                      backgroundColor: `${integration.color}15`,
                      color: integration.color
                    }}
                  >
                    {integration.icon}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mt-0 mb-1">
                      {integration.name}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                      {integration.description}
                    </p>
                  </div>
                </div>
                <div className="ml-15">
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Key Features:</p>
                  <ul className="space-y-1">
                    {integration.features.map((feature, idx) => (
                      <li key={idx} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-2">
                        <span className="text-primary-500 mt-0.5">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
            <h4 className="text-blue-800 dark:text-blue-400 mt-0 mb-2">🔌 Coming Soon</h4>
            <p className="text-blue-700 dark:text-blue-300 mb-0">
              More integrations are being added regularly. Contact support if you need a specific integration 
              for your workflow.
            </p>
          </div>

          <h2>Managing Integrations</h2>

          <h3>Testing Integrations</h3>
          <p>
            After creating an integration, always test it to ensure it works:
          </p>
          <ul>
            <li>Click "Test Connection" button</li>
            <li>Ana will attempt to authenticate with the service</li>
            <li>Verify that the test succeeds before using for deployment</li>
          </ul>

          <h3>Editing Integrations</h3>
          <p>
            You can modify existing integrations:
          </p>
          <ul>
            <li>Update credentials if they've been rotated</li>
            <li>Change default regions or settings</li>
            <li>Add or remove permissions</li>
          </ul>

          <h3>Security Best Practices</h3>
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-8">
            <ul className="space-y-3 mb-0">
              <li>
                <strong>Least Privilege:</strong> Grant only the permissions needed for your use case
              </li>
              <li>
                <strong>Separate Environments:</strong> Use different credentials for dev, staging, and production
              </li>
              <li>
                <strong>Rotate Credentials:</strong> Regularly update access keys and secrets
              </li>
              <li>
                <strong>Monitor Usage:</strong> Review deployment logs and cloud provider audit logs
              </li>
              <li>
                <strong>Enable MFA:</strong> Use multi-factor authentication where possible
              </li>
            </ul>
          </div>

          <h2>Troubleshooting</h2>

          <h3>Connection Test Fails</h3>
          <p>If the connection test fails:</p>
          <ul>
            <li>Verify credentials are correct and not expired</li>
            <li>Check that the service account has necessary permissions</li>
            <li>Ensure network connectivity to the cloud provider APIs</li>
            <li>Verify the account/subscription is active</li>
          </ul>

          <h3>Deployment Fails</h3>
          <p>If deployment fails:</p>
          <ul>
            <li>Check error messages in deployment logs</li>
            <li>Verify integration has permissions for resources being created</li>
            <li>Check for quota limits in your cloud account</li>
            <li>Ensure resource names don't conflict with existing resources</li>
          </ul>

          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-6 rounded-r-lg mt-8">
            <h3 className="text-green-800 dark:text-green-400 mt-0 mb-2">✨ Pro Tip</h3>
            <p className="mb-0 text-green-700 dark:text-green-300">
              Set up separate integrations for development and production environments. This provides 
              better isolation, makes it easier to manage permissions, and helps track costs separately.
            </p>
          </div>
        </div>
      </motion.div>

      <ImageModal
        isOpen={selectedImage !== null}
        src={selectedImage || ''}
        onClose={() => setSelectedImage(null)}
        alt="Screenshot"
      />
    </div>
  );
};

export default Integrations;
